import React, {useEffect, useState} from 'react';
import {Button, Card, Dropdown, DropdownButton, Form, Row, Spinner, Table} from 'react-bootstrap';
import {useNavigate} from 'react-router';
import {Company, ISubscription, SubscriptionStatusEnum} from '../../../model/company/Company';
import {CompanyService} from '../CompanyService';
import {LocalRoutes} from '../../../enum/LocalRoutes';
import {getCorrectEnums, hasAccessRight} from '../../../utils/Functions';
import {PackagesService} from '../../packages/PackagesService';
import {IPackages} from '../../../model/package/IPackage';
import {ErrorToast, SuccessToast} from '../../../utils/toasters';
import ModalAdd from './ModalAdd';
import {calculateFee} from '../../../utils/helpers/FeeCaluculation';
import {useDispatch} from 'react-redux';
import generalSlice from '../../../store/general/GeneralSlice';
import {priceSplitter} from '../../../utils/helpers/NumberFormater';
import {ImpersonateEye} from '../../../shared/components/ImpersonateEye';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import {downloadFileRequest} from '../../../api/api.service';
import {COMPANY_LIST_DOWNLOAD_URL} from '../../../utils/Endpoints';
import TableLayout from '../../../shared/layout/table/TableLayout';
import {ITableColumn} from '../../../shared/components/TableSkeleton';
import {AdminAclEnum} from '../../../enum/AdminAclEnum';
import CompanyRolesAssignModal from '../../brand-access-right/components/AssignBrandRolesModal';
import SingleSelectDropdown from '../../../shared/components/SingleSelectDropdown';

const COMPANY_COLUMNS: ITableColumn[] = [
    {label: 'ID'},
    {label: 'Company'},
    {label: 'Fee'},
    {label: 'Subscription'},
    {label: 'Package'},
    {label: 'Partners'},
    {label: 'Active campaigns'},
    {label: 'Total spent'},
    {label: 'Action', className: 'text-end'},
];

export function CompanyList() {
    const [dataLoading, setDataLoading] = useState<boolean>(true);
    const dispatch = useDispatch();
    const [state, setState] = useState({
        page: 1,
        perPage: 15,
        displayNameOrId: '',
        email: '',
        explicit: true,
        withStats: true,
        totalPages: 0,
    });
    const navigate = useNavigate();
    const [companies, setCompanies] = useState<Company[]>([]);
    const [packagesList, setPackagesList] = useState<IPackages[]>([]);
    const [change, setChange] = useState(false);
    const [modalData, setModalData] = useState({
        showModal: false,
        isClient: undefined,
        title: '',
        companyId: 0,
    });
    const [brandRolesModalData, setBrandRolesModalData] = useState({
        showModal: false,
        companyName: '',
        companyId: 0,
    });
    const [clientFilter, setClientFilter] = useState('');
    const [clientDropdown, setClientDropdown] = useState<Company[]>([]);
    const [supplierFilter, setSupplierFilter] = useState('');
    const [supplierDropdown, setSupplierDropdown] = useState<Company[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setDataLoading(true);
        CompanyService.getCompanies(state)
            .then(data => {
                setCompanies(data.data);
                setState(prev => ({...prev, totalPages: Math.ceil(data.count / state.perPage)}));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));

        PackagesService.getPackages({page: 1, perPage: 99999999})
            .then(data => {
                setPackagesList(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [change]);

    useEffect(() => {
        CompanyService.getPartnershipCompanies({type: 'supplier', search: clientFilter})
            .then(data => {
                setClientDropdown(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [clientFilter]);

    useEffect(() => {
        CompanyService.getPartnershipCompanies({type: 'client', search: supplierFilter})
            .then(data => {
                setSupplierDropdown(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [supplierFilter]);

    const handlePages = (updatePage: number) => {
        setState({...state, page: updatePage});
        setChange(!change);
    };

    function handleClick() {
        setState({...state, page: 1});
        setChange(!change);
    }

    function handleChange(evt: any) {
        const value = evt.target.value.trim();
        setState({
            ...state,
            [evt.target.name]: value,
        });
    }

    const changePackage = (packageId: number, companyId: number) => {
        PackagesService.changePackage(packageId, companyId)
            .then(() => {
                SuccessToast('Successfully added package');
                setChange(!change);
            })
            .catch(err => ErrorToast(err));
    };

    const deleteCompany = (companyId: number) => {
        CompanyService.deleteCompany(companyId)
            .then(() => {
                SuccessToast('Successfully deleted company');
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
                setChange((prev: boolean) => !prev);
            })
            .catch(err => ErrorToast(err));
    };

    const getActiveSubscriptionStatus = (subscriptions: ISubscription[]): SubscriptionStatusEnum | string => {
        return subscriptions.find(sub => sub.isCurrent)?.status ?? '-';
    };

    const closeRolesModal = () => {
      setBrandRolesModalData({
          showModal: false,
          companyName: '',
          companyId: 0,
      })
    }
    return (
        <>
            <ModalAdd {...modalData} setModalData={setModalData}/>

            {/*<Button*/}
            {/*    variant="outline-primary"*/}
            {/*    className="mb-4 w-20"*/}
            {/*    onClick={() => navigate(`/company/create`)}*/}
            {/*>*/}
            {/*    + Add company*/}
            {/*</Button>*/}

            <Card
                className="mb-5 p-4 mt-3"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick();
                }}
            >
                <Row style={{rowGap: '18px'}}>
                    <div className="col-md-2 col-6">
                        <Form.Control placeholder="Company ID" name="id" onChange={handleChange}/>
                    </div>
                    <div className="col-md-2 col-6">
                        <Form.Control placeholder="Company name" name="displayNameOrId" onChange={handleChange}/>
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="All Packages"
                            options={packagesList.map(item => ({ value: String(item.id), label: getCorrectEnums(item.displayName) }))}
                            selected={String((state as any).packageId ?? '')}
                            onChange={value => handleChange({ target: { name: 'packageId', value } })}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="Has Override?"
                            options={[
                                { value: 'true', label: 'Overridden' },
                                { value: 'false', label: 'Not Overridden' },
                            ]}
                            selected={String((state as any).hasOverride ?? '')}
                            onChange={value => handleChange({ target: { name: 'hasOverride', value } })}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Client name or ID"
                            onSelect={setState}
                            setFilter={setClientFilter}
                            keyState={'clientId'}
                            keyItemSelect={'id'}
                            value={clientFilter}
                            items={clientDropdown}
                            hasClearBtn
                            renderItems={['id', 'displayName']}
                            clearFunc={() => {
                                setState((prev: any) => ({...prev, clientId: undefined}));
                                setClientFilter('');
                            }}
                        />
                    </div>
                    <div className="col-md-2 col-6">
                        <AutocompleteInput
                            placeholder="Supplier name or ID"
                            onSelect={setState}
                            setFilter={setSupplierFilter}
                            keyState={'supplierId'}
                            keyItemSelect={'id'}
                            value={supplierFilter}
                            items={supplierDropdown}
                            hasClearBtn
                            renderItems={['id', 'displayName']}
                            clearFunc={() => {
                                setState((prev: any) => ({...prev, supplierId: undefined}));
                                setSupplierFilter('');
                            }}
                        />
                    </div>
                </Row>
                <div className="d-flex gap-2">
                    <Button className="mt-4" disabled={isLoading} onClick={() => handleClick()}>
                        Search
                    </Button>
                    <Button
                        className="mt-4"
                        variant="outline-primary"
                        onClick={() =>
                            downloadFileRequest(COMPANY_LIST_DOWNLOAD_URL, 'export-company', null, state, setIsLoading)
                        }
                        disabled={isLoading || dataLoading}
                    >
                        {isLoading ? (
                            <>
                                Generating excel...{' '}
                                <Spinner animation="border" size="sm" role="status" className="ml-1"/>
                            </>
                        ) : (
                            'Export current'
                        )}
                    </Button>
                </div>
            </Card>

            <TableLayout
                pagination={state}
                listLength={companies.length}
                handlePages={handlePages}
                isLoading={dataLoading}
                dataName={'companies'}
                skeletonColumns={COMPANY_COLUMNS}
                skeletonRows={state.perPage}
            >
                <Table hover responsive>
                    <thead>
                    <tr>
                        {COMPANY_COLUMNS.map(column => (
                            <th key={column.label} className={column.className}>
                                {column.label}
                            </th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {companies.map(e => (
                        <tr key={e.id}>
                            <td>{e.id}</td>
                            <td className="word-break">
                                <ImpersonateEye id={e?.createdBy}/>
                                {e?.displayName}
                            </td>
                            <td>{e?.fee ? calculateFee(e?.fee) : ''}</td>
                            <td>{getActiveSubscriptionStatus(e.subscriptions)}</td>
                            <td>
                                {!e.package && !e.packageOverride && (
                                    <DropdownButton
                                        title="Add"
                                        id="dropdown-menu-align-right}"
                                        variant="outline-primary"
                                    >
                                        {packagesList?.map((item: IPackages, i: number) => {
                                            return (
                                                <Dropdown.Item
                                                    key={i}
                                                    eventKey="1"
                                                    onClick={() => changePackage(item.id, e.id)}
                                                >
                                                    {getCorrectEnums(item?.name)}
                                                </Dropdown.Item>
                                            );
                                        })}
                                    </DropdownButton>
                                )}
                                {!!e.package && !e.packageOverride && (
                                    <DropdownButton
                                        variant="outline-primary"
                                        title={getCorrectEnums(e.package?.displayName ?? '')}
                                    >
                                        {packagesList?.map((item: IPackages, i: number) => {
                                            return (
                                                <Dropdown.Item
                                                    key={i}
                                                    eventKey="1"
                                                    onClick={() => changePackage(item.id, e.id)}
                                                >
                                                    {getCorrectEnums(item?.displayName)}
                                                </Dropdown.Item>
                                            );
                                        })}
                                        <Dropdown.Divider/>
                                        <Dropdown.Item
                                            onClick={() =>
                                                navigate('/packages/override/' + e.id, {
                                                    state: {
                                                        package: e?.package,
                                                        packageOverride: e?.packageOverride,
                                                    },
                                                })
                                            }
                                            eventKey="3"
                                        >
                                            Override
                                        </Dropdown.Item>
                                    </DropdownButton>
                                )}
                                {!!e.package && !!e.packageOverride && (
                                    <Button
                                        variant="outline-primary"
                                        className="mr-1"
                                        onClick={() =>
                                            navigate('/packages/override/' + e.id, {
                                                state: {
                                                    package: e?.package,
                                                    packageOverride: e?.packageOverride,
                                                },
                                            })
                                        }
                                    >
                                        Edit Override ({e?.package?.displayName})
                                    </Button>
                                )}
                            </td>
                            {/*<td>*/}
                            {/*    <Button*/}
                            {/*        className="mr-md-1"*/}
                            {/*        variant="outline-primary"*/}
                            {/*        disabled={e?.package?.globalDiscovery || e?.packageOverride?.globalDiscovery}*/}
                            {/*        onClick={() =>*/}
                            {/*            setModalData((prev: any) => ({*/}
                            {/*                ...prev,*/}
                            {/*                showModal: true,*/}
                            {/*                isClient: true,*/}
                            {/*                title: e.displayName,*/}
                            {/*                companyId: e.id,*/}
                            {/*            }))*/}
                            {/*        }*/}
                            {/*    >*/}
                            {/*        Clients({e?.clientCount})*/}
                            {/*    </Button>*/}
                            {/*</td>*/}
                            {/*<td>*/}
                            {/*    <Button*/}
                            {/*        className="mr-md-1"*/}
                            {/*        variant="outline-primary"*/}
                            {/*        disabled={e?.package?.globalDiscovery || e?.packageOverride?.globalDiscovery}*/}
                            {/*        onClick={() =>*/}
                            {/*            setModalData((prev: any) => ({*/}
                            {/*                ...prev,*/}
                            {/*                showModal: true,*/}
                            {/*                isClient: false,*/}
                            {/*                title: e.displayName,*/}
                            {/*                companyId: e.id,*/}
                            {/*            }))*/}
                            {/*        }*/}
                            {/*    >*/}
                            {/*        Suppliers({e?.supplierCount})*/}
                            {/*    </Button>*/}
                            {/*</td>*/}
                            <td>
                                {!(e?.package?.globalDiscovery || e?.packageOverride?.globalDiscovery) && (
                                    <Button
                                        className="mr-md-1"
                                        variant="outline-primary"
                                        onClick={() =>
                                            setModalData((prev: any) => ({
                                                ...prev,
                                                showModal: true,
                                                isClient: e.isSupplier,
                                                title: e.displayName,
                                                companyId: e.id,
                                            }))
                                        }
                                    >
                                        {e.isSupplier
                                            ? `Clients(${e?.clientCount})`
                                            : `Suppliers(${e?.supplierCount})`}
                                    </Button>
                                )}
                            </td>
                            <td>{e.activeCampaigns ?? '-'}</td>
                            <td>{priceSplitter(e.totalSpent ?? 0)}€</td>
                            <td>
                                <DropdownButton title="Actions" className="d-inline-block mr-1">
                                    <Dropdown.Item onClick={() => navigate(LocalRoutes.COMPANY_EDIT + e?.id)}>
                                        Edit
                                    </Dropdown.Item>
                                    <Dropdown.Divider/>
                                    {hasAccessRight(AdminAclEnum.COMPANY_GROUPS_VIEW) && !e?.package?.globalDiscovery &&
                                        !e?.packageOverride?.globalDiscovery &&
                                        !e.isSupplier &&
                                        (e?.supplierCount ?? 0) > 0 && (
                                            <>
                                                <Dropdown.Item
                                                    onClick={() => navigate(LocalRoutes.COMPANY_GROUP_EDIT + e?.id)}
                                                >
                                                    Edit Groups
                                                </Dropdown.Item>
                                                <Dropdown.Divider/>
                                            </>
                                        )}
                                    {hasAccessRight(AdminAclEnum.BRAND_USER_ACCESS_RIGHTS) && (
                                        <Dropdown.Item
                                            onClick={() =>
                                                setBrandRolesModalData((prev: any) => ({
                                                    showModal: true,
                                                    companyName: e.displayName,
                                                    companyId: e.id,
                                                }))
                                            }
                                        >
                                            Add possible roles
                                        </Dropdown.Item>
                                    )}
                                    {!e?.package?.globalDiscovery && !e?.packageOverride?.globalDiscovery && (
                                        <Dropdown.Item
                                            onClick={() =>
                                                setModalData((prev: any) => ({
                                                    ...prev,
                                                    showModal: true,
                                                    isClient: e.isSupplier,
                                                    title: e.displayName,
                                                    companyId: e.id,
                                                }))
                                            }
                                        >
                                            {e.isSupplier
                                                ? `Clients (${e?.clientCount})`
                                                : `Suppliers (${e?.supplierCount})`}
                                        </Dropdown.Item>
                                    )}

                                    {!e?.package?.globalDiscovery && !e?.packageOverride?.globalDiscovery && (
                                        <Dropdown.Divider/>
                                    )}

                                    <Dropdown.Item onClick={() => navigate(`/company/users/${e.id}`)}>
                                        Users ({e.companyUsersCount ?? 0})
                                    </Dropdown.Item>

                                    <Dropdown.Divider/>

                                    <Dropdown.Item
                                        className="bg-danger-o-50"
                                        onClick={() => {
                                            dispatch(
                                                generalSlice.actions.setConfirmModal({
                                                    show: true,
                                                    contentBody: `Are you sure to delete ${e.displayName} company?`,
                                                    action: () => deleteCompany(e.id),
                                                })
                                            );
                                        }}
                                    >
                                        Delete
                                    </Dropdown.Item>
                                </DropdownButton>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                    {companies.length < 15 && !!companies.length && <div className="h-250px"></div>}
                </Table>
            </TableLayout>
            <CompanyRolesAssignModal show={brandRolesModalData.showModal} closeModal={closeRolesModal} companyId={brandRolesModalData.companyId} companyName={brandRolesModalData.companyName}/>
        </>
    );
}
